/* global globalThis */
import React, { useState } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { getNameVariants } from "../../../utils/maskedData";
import {
  Chip,
  Button,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  Table,
  TableBody,
  Paper,
  Checkbox,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import RoleIcon from "../../../assets/role.svg";
import { fetchAssignne, updateMemberAssignee } from "../../../api/filterType";
import { useLoading } from "../../../context/LoadingContext";
import {
  ArrowDropUp as ArrowDropUpIcon,
  ArrowDropDown as ArrowDropDownIcon,
  LanguageRounded as LanguageRoundedIcon,
  WatchLaterRounded as WatchLaterRoundedIcon,
  PhoneEnabled as PhoneEnabledRoundedIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  TrackChanges as TrackChangesIcon,
  Cancel as CancelIcon,
  EditOutlined as EditOutlinedIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowRight as KeyboardArrowRightIcon,
} from "@mui/icons-material";
import viewIcon from "../../../assets/view.svg";
import biosimilarIcon from "../../../assets/Biosimilars.svg";
import glp1Icon from "../../../assets/GLP 1.svg";
import ecmPIcon from "../../../assets/ECMP.svg";
import copayAssistIcon from "../../../assets/CoPayAssist.svg";
import papIcon from "../../../assets/PAP.svg";
import internationalSourcingIcon from "../../../assets/IntSourcing.svg";
import womensHealthIcon from "../../../assets/WomensHealth.svg";
import diabetesIcon from "../../../assets/Diabetes.svg";
import weightManagementIcon from "../../../assets/WeightManagement.svg";
import { capitalizeFirstLetter } from "../../../utils/capitalize";
import formatDateTimeUtil from "../../../utils/formatDateTime";
import CommonFilterInput from "../commonFilters/commonSingleSelect";
import { iconColors } from "../../../utils/statusColors";
import { usePermissions } from "../../../redux/permissions";
import { getUserPrimaryRole } from "../../../utils/role";

const firstNonEmpty = (...values) => {
  for (const value of values) {
    if (typeof value === "string" && value.trim() !== "") return value;
  }
  return "";
};

const getRecentActivityColor = (activity) =>
  firstNonEmpty(activity?.statusColor, activity?.color) || "#6c757d";

const isNarrowViewport = () =>
  globalThis.window !== undefined && globalThis.window.innerWidth < 2310;

let __ASSIGNEE_CACHE = {
  loaded: false,
  assignees: [],
  names: ["N/A"],
  page: 0,
  hasMore: true,
  searchTerm: "",
};
let __ASSIGNEE_FETCHING = false;
let __ASSIGNEE_VISIT_KEY = null;
const mergeAssigneesIntoCache = (list) => {
  const normalized = list
    .map((u) => {
      if (typeof u === "string") return null;
      const id = u?.id ?? u?.employeeId ?? u?.assigneeId;
      const name = u?.name ?? u?.fullName ?? u?.displayName;
      if (!id || !name) return null;
      return { id, name };
    })
    .filter(Boolean);
  const map = new Map(__ASSIGNEE_CACHE.assignees.map((a) => [a.id, a]));
  for (const n of normalized) {
    map.set(n.id, n);
  }
  const merged = Array.from(map.values());
  __ASSIGNEE_CACHE.assignees = merged;
  __ASSIGNEE_CACHE.names = merged.length ? merged.map((u) => u.name) : ["N/A"];
};
const isMemberOutreachRoute = () => {
  return (
    typeof globalThis !== "undefined" &&
    globalThis.location &&
    typeof globalThis.location.pathname === "string" &&
    globalThis.location.pathname.includes("/member-outreach")
  );
};
const processAssigneeResponse = (res) => {
  let list = [];

  if (Array.isArray(res)) {
    list = res;
  } else if (Array.isArray(res?.data)) {
    list = res.data;
  } else if (Array.isArray(res?.results)) {
    list = res.results;
  } else if (Array.isArray(res?.content)) {
    list = res.content;
  }

  return list;
};
const ensureAssigneesPrefetched = async (visitKey, searchTerm = "") => {
  if (isMemberOutreachRoute()) {
    if (visitKey && __ASSIGNEE_VISIT_KEY !== visitKey) {
      __ASSIGNEE_CACHE = {
        loaded: false,
        assignees: [],
        names: ["N/A"],
        page: 0,
        hasMore: true,
        searchTerm: "",
      };
      __ASSIGNEE_VISIT_KEY = visitKey;
    }
    if (__ASSIGNEE_CACHE.loaded && __ASSIGNEE_CACHE.searchTerm === searchTerm) {
      return __ASSIGNEE_CACHE;
    }
  } else if (
    __ASSIGNEE_CACHE.loaded &&
    __ASSIGNEE_CACHE.searchTerm === searchTerm
  ) {
    return __ASSIGNEE_CACHE;
  }
  if (__ASSIGNEE_FETCHING) return __ASSIGNEE_CACHE;
  __ASSIGNEE_FETCHING = true;
  try {
    const res = await fetchAssignne({
      search: searchTerm || undefined,
    });
    const list = processAssigneeResponse(res);

    mergeAssigneesIntoCache(list);
    __ASSIGNEE_CACHE.page = 0;
    __ASSIGNEE_CACHE.hasMore = list.length >= 30;
    __ASSIGNEE_CACHE.searchTerm = searchTerm;
    __ASSIGNEE_CACHE.loaded = true;
  } catch (e) {
    // Failed to prefetch assignees; update cache state to indicate no more data
    console.debug("Failed to prefetch assignees:", e);
    __ASSIGNEE_CACHE.hasMore = false;
    __ASSIGNEE_CACHE.loaded = true;
  } finally {
    __ASSIGNEE_FETCHING = false;
  }
  return __ASSIGNEE_CACHE;
};

export const createHeaderWithSort = (title, showSortIcons = false) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: "4px",
      cursor: "default",
    }}
    onClickCapture={(e) => {
      try {
        const target = e.target;
        // allow clicks originating on elements marked with data-sort-icon
        if (!target?.closest?.("[data-sort-icon]")) {
          e.stopPropagation();
        }
      } catch (err) {
        // DOM API may fail; ensure event propagation is stopped
        console.debug("DOM API error during event handling:", err);
        e.stopPropagation();
      }
    }}
    onKeyDownCapture={(e) => {
      if (e.key === "Enter" || e.key === " ") {
        try {
          const target = e.target;
          if (!target?.closest?.("[data-sort-icon]")) {
            e.stopPropagation();
          }
        } catch (err) {
          console.debug("DOM API error during event handling:", err);
          e.stopPropagation();
        }
      }
    }}
  >
    <span
      style={{
        fontStyle: "normal",
        fontWeight: 550,
        fontSize: "16px",
        lineHeight: "24px",
        letterSpacing: "0px",
        color: "#4b3f72",
        textTransform: "none",
        whiteSpace: "nowrap important",
      }}
    >
      {title}
    </span>
    {showSortIcons && (
      <div
        style={{ display: "flex", flexDirection: "column", marginLeft: "4px" }}
      >
        <ArrowDropUpIcon
          data-sort-icon
          style={{
            fontSize: "16px",
            color: "inherit",
            lineHeight: 0,
            opacity: 0.8,
            cursor: "pointer",
          }}
        />
        <ArrowDropDownIcon
          data-sort-icon
          style={{
            fontSize: "16px",
            color: "inherit",
            marginTop: "-8px",
            lineHeight: 0,
            opacity: 0.8,
            cursor: "pointer",
          }}
        />
      </div>
    )}
  </div>
);

export const memberColumn = {
  header: createHeaderWithSort("Member Details", true),
  accessorKey: "member",
  size: 140,
  id: "member",
  sortingFn: (rowA, rowB) => {
    const a = rowA.original.member?.name || "";
    const b = rowB.original.member?.name || "";
    return a.localeCompare(b);
  },
  cell: (cell) => {
    const { row, column } = cell;
    const member = row.original.member || row.original; // fallback for old structure
    const showPHI = column?.columnDef?.meta?.showPHI || false;
    const memberId = member.memberId || member.id || "";

    const onQuickView = row.original.onQuickView;
    const memberBadge = row.original.memberBadge;

    const shouldShowBadge =
      memberBadge && globalThis.location.pathname.includes("/all-members");
    let source = "members";
    const pathname = globalThis.location.pathname;

    if (pathname.includes("/monitoring")) {
      source = "monitoring";
    } else if (pathname.includes("/member-outreach")) {
      source = "member-outreach";
    } else if (
      pathname.includes("/all-members") ||
      pathname.includes("/pharmacyProfile") ||
      pathname.includes("/providerProfile")
    ) {
      source = "all-members";
    }

    return (
      <div
        className="member-cell"
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 4,
          minWidth: "140px",
          maxWidth: "none",
          width: "auto",
          whiteSpace: "nowrap",
          paddingLeft: row.depth > 0 ? 32 : 0,
        }}
      >
        {shouldShowBadge && (
          <div
            style={{
              backgroundColor: "#E8E4F3",
              color: "#4B3F72",
              padding: "4px 4px",
              borderRadius: "4px",
              fontSize: "12px",
              fontWeight: 600,
              whiteSpace: "nowrap",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              boxSizing: "border-box",
              lineHeight: "16px",
              marginRight: "8px",
              marginBottom: "4px",
              minWidth: "fit-content",
              width: "auto",
            }}
          >
            {memberBadge}
          </div>
        )}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            flexWrap: "nowrap",
            width: "100%",
          }}
        >
          <Tooltip
            title={
              showPHI
                ? member.name || ""
                : getNameVariants(member.name, showPHI).maskedMiddle || ""
            }
            placement="bottom-start"
            arrow
            describeChild
            disableInteractive
            slotProps={{
              popper: {
                sx: {
                  "& .MuiTooltip-tooltip": {
                    backgroundColor: "#1c1c1f",
                    color: "#FFFFFF",
                    margin: 0,
                    fontSize: "12px",
                    lineHeight: 1.3,
                  },
                  "& .MuiTooltip-arrow": {
                    color: "#1c1c1f",
                    margin: 0,
                    left: "50% !important",
                    transform: "translateX(-50%) !important",
                  },
                },
              },
              tooltip: {
                sx: {
                  margin: 0,
                  marginTop: "0px !important",
                  padding: "4px 8px",
                },
              },
            }}
          >
            <Link
              to={`/members/${memberId}`}
              state={{
                fromMemberMonitoring:
                  globalThis.location.pathname.includes("/monitoring"),
                fromPriorityOutreach:
                  globalThis.location.pathname.includes("/member-outreach"),

                fromAllMembers:
                  globalThis.location.pathname.includes("/member-outreach") ||
                  globalThis.location.pathname.includes("/pharmacyProfile") ||
                  globalThis.location.pathname.includes("/providerProfile"),
                memberBadge:
                  memberBadge === "Graduation Completed" ||
                  memberBadge === "Member Monitoring",
                memberBadgeValue: memberBadge,
                showDisposition: memberBadge !== "Graduation Completed",
                source: source,
              }}
              style={{
                fontWeight: 600,
                fontStyle: "Bold",
                fontSize: "17px",
                color: "#4b3f72",
                flexDirection: "column",
                textDecoration: "underline",
                textDecorationStyle: "solid",
                textDecorationThickness: 1,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                textDecorationSkipInk: "auto",
                textUnderlineOffset: 3,
                cursor: "pointer",
                flex: 1,
                minWidth: "100px",
                display: "inline-block",
                marginTop: "4px",
              }}
              onClick={() => {
                if (
                  globalThis.location.pathname.includes("/pharmacyProfile") ||
                  globalThis.location.pathname.includes("/providerProfile")
                ) {
                  try {
                    localStorage.setItem(
                      "nav_activeItem",
                      JSON.stringify("Members"),
                    );
                    localStorage.setItem(
                      "nav_activeSubItem",
                      JSON.stringify("All Members"),
                    );
                    localStorage.setItem(
                      "nav_openSubmenus",
                      JSON.stringify({ Members: true }),
                    );
                  } catch (err) {
                    // Navigation or localStorage operation failed; error already logged
                    if (err instanceof Error) {
                      // Error already logged
                    }
                  }

                  globalThis.dispatchEvent(
                    new Event("updateNavFromProfileOpportunities"),
                  );
                }
              }}
            >
              {showPHI
                ? member.name || ""
                : getNameVariants(member.name, showPHI).initialsOnly}
            </Link>
          </Tooltip>
          {onQuickView && (
            <Button
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                onQuickView(row.original);
              }}
              sx={{
                textTransform: "none",
                fontWeight: 500,
                color: "#403C6F",
                backgroundColor: "transparent",
                border: "1px solid #E9E9E8",
                borderRadius: "6px",
                px: 1.5,
                py: 0.5,
                minWidth: "100px",
                height: "32px",
                fontSize: "0.8125rem",
                boxShadow: "none",
                whiteSpace: "nowrap",
                flexShrink: 0,
                "&:hover": {
                  backgroundColor: "#F9F9F9",
                  cursor: "pointer",
                },
                "&:active": {
                  transform: "scale(0.98)",
                },
                transition: "all 0.2s ease-in-out",
              }}
            >
              <img
                src={viewIcon}
                alt="View"
                style={{ marginRight: 4, width: 16, height: 16 }}
              />
            </Button>
          )}
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            fontWeight: 500,
            fontSize: "10px",
            lineHeight: "150%",
            letterSpacing: "0%",
            color: "#403C6F",
            width: "100%",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 4,
              width: "30%",
            }}
          >
            <Tooltip title="Timezone" arrow>
              <WatchLaterRoundedIcon
                style={{ fontSize: 14, color: "1B2F3F" }}
              />
            </Tooltip>
            <span style={{ fontSize: "11px" }}>
              {member.timeZone || member.timezone || "N/A"}
            </span>
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 4,
              width: "70%",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            <Tooltip title="Language" arrow>
              <LanguageRoundedIcon style={{ fontSize: 14, color: "#1B2F3F" }} />
            </Tooltip>
            <Tooltip
              title={member.language ? member.language : "N/A"}
              placement="bottom-start"
              arrow
              describeChild
              disableInteractive
              slotProps={{
                popper: {
                  sx: {
                    "& .MuiTooltip-tooltip": {
                      backgroundColor: "#1c1c1f",
                      color: "#FFFFFF",
                      margin: 0,
                      fontSize: "12px",
                      lineHeight: 1.3,
                    },
                    "& .MuiTooltip-arrow": {
                      color: "#1c1c1f",
                      margin: 0,
                      left: "50% !important",
                      transform: "translateX(-50%) !important",
                    },
                  },
                },
                tooltip: {
                  sx: {
                    margin: 0,
                    marginTop: "0px !important",
                    padding: "4px 8px",
                  },
                },
              }}
            >
              <span
                style={{
                  fontSize: "11px",
                  maxWidth: "70px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  display: "inline-block",
                  verticalAlign: "middle",
                }}
              >
                {member.language?.trim()?.toUpperCase() === "N/A"
                  ? "N/A"
                  : member.language}
              </span>
            </Tooltip>
          </div>
        </div>
      </div>
    );
  },
  enableSorting: true,
};

// The outreach payload already tells us which family members are enrolled -
// anyone sharing this member's subscriberNum who also has their own outreach
// item is a HALO member (mapOutreachItem in api/priority.js derives this
// list). No separate API call is needed to render this cell.
const FamilyCell = ({ row, column }) => {
  const member = row.original.member || row.original;
  const showPHI = column?.columnDef?.meta?.showPHI || false;
  const familyMembers = Array.isArray(member.familyMembers)
    ? member.familyMembers
    : [];

  const textStyle = { fontSize: "13px", color: "#9F9DB6" };

  if (familyMembers.length === 0) {
    return <span style={textStyle}>—</span>;
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      {familyMembers.map((m) => (
        <Tooltip
          key={m.memberId}
          title={
            showPHI
              ? m.name || ""
              : getNameVariants(m.name, showPHI).maskedMiddle || ""
          }
          placement="bottom-start"
          arrow
          describeChild
          disableInteractive
          slotProps={{
            popper: {
              sx: {
                "& .MuiTooltip-tooltip": {
                  backgroundColor: "#1c1c1f",
                  color: "#FFFFFF",
                  margin: 0,
                  fontSize: "12px",
                  lineHeight: 1.3,
                },
                "& .MuiTooltip-arrow": {
                  color: "#1c1c1f",
                  margin: 0,
                  left: "50% !important",
                  transform: "translateX(-50%) !important",
                },
              },
            },
            tooltip: {
              sx: {
                margin: 0,
                marginTop: "0px !important",
                padding: "4px 8px",
              },
            },
          }}
        >
          <Link
            to={`/members/${m.memberId}`}
            style={{
              fontSize: "13px",
              fontWeight: 600,
              color: "#4b3f72",
              textDecoration: "underline",
              cursor: "pointer",
            }}
          >
            {showPHI
              ? m.name || ""
              : getNameVariants(m.name, showPHI).initialsOnly}
          </Link>
        </Tooltip>
      ))}
    </div>
  );
};

FamilyCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.object,
  }).isRequired,
  column: PropTypes.shape({
    columnDef: PropTypes.shape({
      meta: PropTypes.shape({
        showPHI: PropTypes.bool,
      }),
    }),
  }),
};

// Leading unlabeled column holding the expand/collapse toggle for a primary
// member's family sub-rows - blank for rows with no family members.
export const expanderColumn = {
  id: "expander",
  header: () => null,
  size: 48,
  enableSorting: false,
  cell: ({ row }) =>
    row.getCanExpand?.() ? (
      <IconButton
        size="small"
        aria-label={
          row.getIsExpanded()
            ? "Collapse family members"
            : "Expand family members"
        }
        onClick={(e) => {
          e.stopPropagation();
          row.toggleExpanded();
        }}
        sx={{ p: 0.25, color: "#403C6F" }}
      >
        {row.getIsExpanded() ? (
          <KeyboardArrowDownIcon fontSize="small" />
        ) : (
          <KeyboardArrowRightIcon fontSize="small" />
        )}
      </IconButton>
    ) : null,
};

export const familyColumn = {
  header: createHeaderWithSort("Family", false),
  accessorKey: "family",
  id: "family",
  size: 160,
  enableSorting: false,
  cell: ({ row, column }) => <FamilyCell row={row} column={column} />,
};

// Priority Column
export const priorityColumn = {
  header: createHeaderWithSort("Priority", true),
  accessorKey: "priorityLevel",
  id: "priorityLevel",
  size: 110,
  enableSorting: true,
  sortingFn: (rowA, rowB) => {
    const a = String(
      rowA.original.member?.priorityLevel || rowA.original.priorityLevel || "",
    );
    const b = String(
      rowB.original.member?.priorityLevel || rowB.original.priorityLevel || "",
    );
    return a.localeCompare(b);
  },
  cell: ({ row }) => {
    const member = row.original.member || row.original;
    const pLevel = String(
      member.priorityLevel || row.original.priorityLevel || "",
    )
      .toLowerCase()
      .trim();
    let priorityPill = null;
    if (pLevel.includes("1")) {
      priorityPill = {
        text: "PRIORITY 1",
        bgColor: "#FFF5F5",
        textColor: "#D32F2F",
        borderColor: "#FFCDD2",
      };
    } else if (pLevel.includes("2")) {
      priorityPill = {
        text: "PRIORITY 2",
        bgColor: "#FFF9E6",
        textColor: "#B45309",
        borderColor: "#FFE082",
      };
    } else if (pLevel.includes("3")) {
      priorityPill = {
        text: "PRIORITY 3",
        bgColor: "#EEF2FF",
        textColor: "#4F46E5",
        borderColor: "#C5CAE9",
      };
    }

    if (!priorityPill) return "N/A";

    return (
      <span
        style={{
          backgroundColor: priorityPill.bgColor,
          color: priorityPill.textColor,
          border: `1px solid ${priorityPill.borderColor}`,
          padding: "2px 8px",
          borderRadius: "10px",
          fontSize: "11px",
          fontWeight: 700,
          textTransform: "uppercase",
          display: "inline-flex",
          alignItems: "center",
          lineHeight: "1",
          height: "20px",
          boxSizing: "border-box",
          letterSpacing: "0.5px",
          flexShrink: 0,
          whiteSpace: "nowrap",
        }}
      >
        {priorityPill.text}
      </span>
    );
  },
};

// Provider Column
export const providerColumn = {
  header: createHeaderWithSort("Provider", true),
  accessorKey: "provider",
  size: 130,
  enableSorting: true,
  sortingFn: (rowA, rowB) => {
    const a = rowA.original.provider?.name || "";
    const b = rowB.original.provider?.name || "";
    return a.localeCompare(b);
  },
  cell: ({ row }) => {
    const provider =
      row.original.provider || row.original.providerDetails || row.original;

    return (
      <div
        style={{
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          fontWeight: 500,
          fontSize: "14px",
          lineHeight: "150%",
          letterSpacing: "0%",
          minWidth: "130px",
          maxWidth: "130px",
          width: "130px",
        }}
      >
        <Tooltip
          title={provider.name || ""}
          placement="bottom-start"
          arrow
          describeChild
          disableInteractive
          slotProps={{
            popper: {
              sx: {
                "& .MuiTooltip-tooltip": {
                  backgroundColor: "#343435",
                  color: "#FFFFFF",
                  margin: 0,
                },
                "& .MuiTooltip-arrow": {
                  color: "#343435",
                  margin: 0,
                  left: "50% !important",
                  transform: "translateX(-50%) !important",
                },
              },
            },
            tooltip: {
              sx: {
                margin: 0,
                marginTop: "0px !important",
                padding: "4px 8px",
              },
            },
          }}
        >
          <div
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              fontWeight: 500,
              fontSize: "16px",
              lineHeight: "150%",
              letterSpacing: "0%",
            }}
          >
            {provider.name}
          </div>
        </Tooltip>
        {provider.contact && (
          <div
            style={{
              display: "flex",
              gap: 6,
              alignItems: "center",
              fontWeight: 500,
              fontSize: "14px",
              lineHeight: "150%",
              letterSpacing: "0%",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            <Tooltip title="Phone" arrow>
              <PhoneEnabledRoundedIcon
                style={{ fontSize: 16, minWidth: "16px" }}
              />
            </Tooltip>
            <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
              {provider.contact}
            </span>
          </div>
        )}
      </div>
    );
  },
};

export const assigneeColumn = {
  header: createHeaderWithSort("Assignee", true),
  accessorKey: "assignee",
  size: isNarrowViewport() ? 80 : 100,
  minSize: isNarrowViewport() ? 70 : 90,
  maxSize: isNarrowViewport() ? 120 : 150,
  enableSorting: true,
  sortingFn: (rowA, rowB) => {
    const a = rowA.original.assignee?.name || "";
    const b = rowB.original.assignee?.name || "";
    return a.localeCompare(b);
  },
  cell: ({ row }) => <AssigneeCell row={row} visitKey={undefined} />,
};
export const createAssigneeColumn = (visitKey, isBulkModeActive = false) => ({
  ...assigneeColumn,
  cell: ({ row }) => (
    <AssigneeCell
      row={row}
      visitKey={visitKey}
      isBulkModeActive={isBulkModeActive}
    />
  ),
});

const AssigneeCell = ({ row, visitKey, isBulkModeActive = false }) => {
  const { showLoading, hideLoading } = useLoading();
  const assignee = row.original.assignee || row.original;
  const userDetails = JSON.parse(localStorage.getItem("userDetails"));
  const role = userDetails?.primaryRoleName;
  const { canWrite: canWriteMemberOutreach } =
    usePermissions("Member Outreach");

  const [assignees, setAssignees] = useState(
    assignee?.id && assignee?.name
      ? [{ id: assignee.id, name: assignee.name }]
      : [],
  );
  const [names, setNames] = useState(
    assignee?.name ? [assignee.name] : ["Unassigned"],
  );
  const [loaded, setLoaded] = useState(false);
  const [currentName, setCurrentName] = useState(
    assignee?.name || "Unassigned",
  );

  const memberId =
    row.original.member?.memberId ||
    row.original.member?.id ||
    row.original.memberId ||
    row.original.id;
  // A primary subscriber with no outreach program of their own has no real
  // numeric member id - the backend gives them a synthetic
  // "{subscriberNum}-{personCode}" id instead (see api/priority.js). If a
  // family member under the same subscriber does have a real id (i.e. has an
  // open opportunity of their own), assign through them instead - the same
  // "borrow a sibling's real id" pattern fetchMemberOverviewBySyntheticId
  // uses to backfill a programless primary's account-level fields. Only when
  // no one in the family has an opportunity is there truly nothing to assign.
  const hasSyntheticId = String(memberId || "").includes("-");
  const familyMembers = row.original.member?.familyMembers || [];
  const assignableFamilyMember = familyMembers.find(
    (m) => m.memberId && !String(m.memberId).includes("-"),
  );
  const isNonMember = hasSyntheticId && !assignableFamilyMember;
  // Assignment is family-wide and happens through the primary - a family
  // sub-row's dropdown is read-only.
  const isFamilyRow = row.depth > 0 || Boolean(row.original.isFamilyMember);
  // Assignment is family-wide and happens through the primary - a family
  // sub-row's dropdown is read-only.
  const assignmentTargetId =
    hasSyntheticId && assignableFamilyMember
      ? assignableFamilyMember.memberId
      : memberId;
  // Assignment is family-wide: whatever is set on the primary is applied to
  // every family member in the outreach list too (each real member id gets
  // its own PATCH - a synthetic "{subscriberNum}-{personCode}" id isn't a real
  // member and can't be assigned to).
  const assignmentTargetIds = [
    ...new Set(
      [
        assignmentTargetId,
        ...familyMembers.map((m) => m.memberId),
        ...(row.original.subRows || []).map((s) => s.member?.memberId),
      ]
        .filter((id) => id && !String(id).includes("-"))
        .map(String),
    ),
  ];
  React.useEffect(() => {
    setCurrentName(assignee?.name || "Unassigned");
  }, [assignee?.id, assignee?.name]);

  React.useEffect(
    () => {
      let mounted = true;
      void (async () => {
        const cache = await ensureAssigneesPrefetched(visitKey, "");
        if (!mounted) return;
        setAssignees(cache.assignees);
        setNames(cache.names);
        setLoaded(cache.loaded);
      })();
      return () => {
        mounted = false;
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const handleOpen = async () => {
    if (!loaded) {
      const cache = await ensureAssigneesPrefetched(visitKey, "");
      setAssignees(cache.assignees);
      setNames(cache.names);
      setLoaded(cache.loaded);
    }
  };

  // Helper: Check if selectedName is empty/null/undefined
  const isEmptySelection = (selectedName) => {
    return (
      selectedName === null || selectedName === "" || selectedName === undefined
    );
  };

  // Helper: Invalidate assignee cache
  const invalidateAssigneeCache = () => {
    __ASSIGNEE_CACHE.loaded = false;
    __ASSIGNEE_CACHE.assignees = [];
    __ASSIGNEE_CACHE.names = ["N/A"];
    __ASSIGNEE_CACHE.page = 0;
    __ASSIGNEE_CACHE.hasMore = true;
    __ASSIGNEE_CACHE.searchTerm = "";
  };

  // Helper: Dispatch assignee updated event
  // memberIds/assigneeName let every other assignee cell showing one of those
  // members (the primary's family sub-rows) refresh its displayed value too.
  const dispatchAssigneeUpdated = (memberId, assigneeId, assigneeName) => {
    if (globalThis?.dispatchEvent) {
      globalThis.dispatchEvent(
        new CustomEvent("assignee-updated", {
          detail: {
            memberId,
            assigneeId,
            assigneeName,
            memberIds: assignmentTargetIds,
          },
        }),
      );
    }
  };

  React.useEffect(() => {
    const onAssigneeUpdated = (event) => {
      const { memberIds, assigneeName } = event.detail || {};
      if (!Array.isArray(memberIds) || !assigneeName) return;
      if (memberIds.includes(String(memberId))) {
        setCurrentName(assigneeName);
      }
    };
    globalThis.addEventListener?.("assignee-updated", onAssigneeUpdated);
    return () =>
      globalThis.removeEventListener?.("assignee-updated", onAssigneeUpdated);
  }, [memberId]);

  // Helper: Handle unassigning a member
  const handleUnassign = async () => {
    showLoading?.();
    await Promise.all(
      assignmentTargetIds.map((id) => updateMemberAssignee(id, null)),
    );
    setCurrentName("Unassigned");
    try {
      invalidateAssigneeCache();
      // One event is enough - listeners just refetch the assignee options.
      dispatchAssigneeUpdated(assignmentTargetIds[0], null, "Unassigned");
    } catch (error_) {
      // Cache invalidation or event dispatch failed; error already logged
      if (error_ instanceof Error) {
        // Error already logged
      }
    }
  };

  // Helper: Handle assigning a member
  const handleAssign = async (assigneeId, selectedName) => {
    showLoading?.();

    await Promise.all(
      assignmentTargetIds.map((id) => updateMemberAssignee(id, assigneeId)),
    );
    setCurrentName(selectedName);
    try {
      invalidateAssigneeCache();
      dispatchAssigneeUpdated(assignmentTargetIds[0], assigneeId, selectedName);
    } catch (error_) {
      // Cache invalidation or event dispatch failed
      console.debug("Cache invalidation or event dispatch failed:", error_);
    }
  };

  const handleChange = async (selectedName) => {
    try {
      // Early return if there's no real member (self or borrowed from family)
      // to attach an assignee to
      if (!assignmentTargetId) return;

      // Handle empty/null/undefined selection
      if (isEmptySelection(selectedName)) {
        await handleUnassign();
        return;
      }

      // Skip invalid selections
      if (!selectedName || selectedName === "N/A") return;

      // Handle explicit Unassigned selection
      if (selectedName === "Unassigned") {
        await handleUnassign();
        return;
      }

      // Find matching assignee
      const match = assignees.find((a) => a.name === selectedName);
      if (!match) return;

      // Handle assignment based on match.id
      if (match.id == null || match.id === "") {
        await handleUnassign();
      } else {
        await handleAssign(match.id, selectedName);
      }
    } catch (error_) {
      // Error already handled in handleUnassign/handleAssign; cleanup continues in finally
      console.debug("Error in handleChange:", error_);
    } finally {
      hideLoading?.();
    }
  };

  if (
    (role === "L6" || role === "L7" || role === "L8" || role === "L9") &&
    canWriteMemberOutreach &&
    !isBulkModeActive
  ) {
    const assigneeInput = (
      <div style={{ minWidth: "160px", maxWidth: "240px", width: "100%" }}>
        <div
          style={
            isNonMember || isFamilyRow
              ? { pointerEvents: "none", opacity: 0.5 }
              : undefined
          }
        >
          <CommonFilterInput
            type="radio-select"
            label="Assignee"
            showLabel={false}
            options={names}
            value={currentName}
            onChange={handleChange}
            onOpen={handleOpen}
            filterType="ASSIGNEE"
            instanceId={`ASSIGNEE_${memberId}`}
          />
        </div>
      </div>
    );

    return isNonMember || isFamilyRow ? (
      <Tooltip
        title={
          isFamilyRow
            ? "Assign through the primary member"
            : "No open opportunities"
        }
        arrow
        placement="bottom-start"
      >
        <span style={{ display: "inline-block", width: "100%" }}>
          {assigneeInput}
        </span>
      </Tooltip>
    ) : (
      assigneeInput
    );
  }

  return (
    <Tooltip
      title={assignee?.name || currentName || "Unassigned"}
      placement="bottom-start"
      arrow
      describeChild
      disableInteractive
      slotProps={{
        popper: {
          sx: {
            "& .MuiTooltip-tooltip": {
              backgroundColor: "#343435",
              color: "#FFFFFF",
              margin: 0,
            },
            "& .MuiTooltip-arrow": {
              color: "#343435",
              margin: 0,
              left: "50% !important",
              transform: "translateX(-50%) !important",
            },
          },
        },
        tooltip: {
          sx: {
            margin: 0,
            marginTop: "0px !important",
            padding: "4px 8px",
          },
        },
      }}
    >
      <div
        style={{
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          fontWeight: 500,
          fontSize: "16px",
          lineHeight: "150%",
          letterSpacing: "0%",
        }}
      >
        {assignee?.name || currentName || "Unassigned"}
      </div>
    </Tooltip>
  );
};

export const statusColumn = {
  header: createHeaderWithSort("Status", true),
  accessorKey: "status",
  size: 120,
  enableSorting: true,
  sortingFn: (rowA, rowB) => {
    const statusA = rowA.original?.recentActivity?.programStatus || "N/A";
    const statusB = rowB.original?.recentActivity?.programStatus || "N/A";
    return statusA.localeCompare(statusB);
  },
  cell: ({ row }) => {
    // A member with no program/opportunity (e.g. a primary subscriber whose
    // only presence is their family's) has no program status to show.
    const hasNoPrograms =
      Array.isArray(row.original?.programs) &&
      row.original.programs.length === 0;
    const status = hasNoPrograms
      ? "N/A"
      : capitalizeFirstLetter(row.original?.recentActivity?.programStatus) ||
        "N/A";
    return <div style={{ fontWeight: 500, fontSize: "14px" }}>{status}</div>;
  },
};

export const cagColumn = {
  header: createHeaderWithSort("CAG", true),
  accessorKey: "cag",
  size: 130,
  enableSorting: true,
  sortingFn: (rowA, rowB) => {
    const a = rowA.original.cag?.name || "";
    const b = rowB.original.cag?.name || "";
    return a.localeCompare(b);
  },
  cell: ({ row }) => {
    const cag = row.original.cag || row.original;
    let width = "auto";
    let maxWidth = "none";

    if (globalThis !== undefined && globalThis.innerWidth < 1430) {
      width = "180px";
      maxWidth = "180px";
    } else if (globalThis !== undefined && globalThis.innerWidth < 1600) {
      width = "300px";
      maxWidth = "300px";
    }

    return (
      <div
        className="cag-cell"
        style={{
          whiteSpace: "nowrap",
          fontWeight: 500,
          fontSize: "clamp(12px, 1vw, 14px)",
          lineHeight: "150%",
          letterSpacing: "0%",
          minWidth: "120px",
          width: width,
          maxWidth: maxWidth,
        }}
      >
        <Tooltip
          title={cag.name || ""}
          placement="bottom-start"
          arrow
          describeChild
          disableInteractive
          slotProps={{
            popper: {
              sx: {
                "& .MuiTooltip-tooltip": {
                  backgroundColor: "#343435",
                  color: "#FFFFFF",
                  margin: 0,
                },
                "& .MuiTooltip-arrow": {
                  color: "#343435",
                  margin: 0,
                  left: "50% !important",
                  transform: "translateX(-50%) !important",
                },
              },
            },
            tooltip: {
              sx: {
                margin: 0,
                marginTop: "0px !important",
                padding: "4px 8px",
              },
            },
          }}
        >
          <div
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              fontWeight: 500,
              lineHeight: "150%",
              fontSize: "clamp(14px, 1.1vw, 16px)",
              letterSpacing: "0%",
            }}
          >
            {cag.name}
          </div>
        </Tooltip>
        {cag.contact && (
          <div
            style={{
              display: "flex",
              gap: 6,
              alignItems: "center",
              fontWeight: 500,
              lineHeight: "150%",
              letterSpacing: "0%",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              fontSize: "clamp(12px, 1vw, 14px)",
            }}
          >
            <Tooltip
              title={cag.contact || ""}
              placement="bottom-start"
              arrow
              describeChild
              disableInteractive
              slotProps={{
                popper: {
                  sx: {
                    "& .MuiTooltip-tooltip": {
                      backgroundColor: "#343435",
                      color: "#FFFFFF",
                      margin: 0,
                    },
                    "& .MuiTooltip-arrow": {
                      color: "#343435",
                      margin: 0,
                      left: "50% !important",
                      transform: "translateX(-50%) !important",
                    },
                  },
                },
                tooltip: {
                  sx: {
                    margin: 0,
                    marginTop: "0px !important",
                    padding: "4px 8px",
                  },
                },
              }}
            >
              <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                {cag.contact}
              </span>
            </Tooltip>
          </div>
        )}
      </div>
    );
  },
};

export const pharmacyColumn = {
  header: createHeaderWithSort("Pharmacy", true),
  accessorKey: "pharmacy",
  size: 120,
  enableSorting: true,
  sortingFn: (rowA, rowB) => {
    const a = rowA.original.pharmacy?.name || "";
    const b = rowB.original.pharmacy?.name || "";
    return a.localeCompare(b);
  },
  cell: ({ row }) => {
    const pharmacy =
      row.original.pharmacy || row.original.pharmacyDetails || row.original;

    return (
      <div
        style={{
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          fontWeight: 500,
          fontSize: "14px",
          lineHeight: "150%",
          letterSpacing: "0%",
          minWidth: "120px",
          maxWidth: "120px",
          width: "120px",
        }}
      >
        <Tooltip
          title={pharmacy.name || ""}
          placement="bottom-start"
          arrow
          describeChild
          disableInteractive
          slotProps={{
            popper: {
              sx: {
                "& .MuiTooltip-tooltip": {
                  backgroundColor: "#343435",
                  color: "#FFFFFF",
                  margin: 0,
                },
                "& .MuiTooltip-arrow": {
                  color: "#343435",
                  margin: 0,
                  left: "50% !important",
                  transform: "translateX(-50%) !important",
                },
              },
            },
            tooltip: {
              sx: {
                margin: 0,
                marginTop: "0px !important",
                padding: "4px 8px",
              },
            },
          }}
        >
          <div
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              fontWeight: 500,
              fontSize: "16px",
              lineHeight: "150%",
              letterSpacing: "0%",
            }}
          >
            {capitalizeFirstLetter(pharmacy.name)}
          </div>
        </Tooltip>
        {pharmacy.contact && (
          <div
            style={{
              display: "flex",
              gap: 6,
              alignItems: "center",
              fontWeight: 500,
              fontSize: "14px",
              lineHeight: "150%",
              letterSpacing: "0%",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            <Tooltip title="Phone" arrow>
              <PhoneEnabledRoundedIcon
                style={{ fontSize: 16, minWidth: "16px" }}
              />
            </Tooltip>
            <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
              {pharmacy.contact}
            </span>
          </div>
        )}
      </div>
    );
  },
};

const getProgramStatus = (program) => {
  if (typeof program === "string") {
    if (program.toLowerCase().includes("weight")) return "enrolled";
    if (program.toLowerCase().includes("international")) return "declined";
    return "eligible";
  }
  return "eligible";
};

AssigneeCell.propTypes = {
  row: PropTypes.object,
  visitKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

const ProgramsPopup = React.memo(({ open, onClose, memberName, programs }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const formatActivityText = (program) => {
    const activity = program?.recentActivity;

    if (!activity) {
      return (
        <div style={{ color: "#1B2F3F", fontWeight: 500, fontSize: "14px" }}>
          No recent activity
        </div>
      );
    }

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        {activity.status && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "4px",
              color: getRecentActivityColor(activity),
              fontWeight: 600,
              fontSize: "14px",
            }}
          >
            <span
              style={{
                whiteSpace: "nowrap",
                fontWeight: 600,
                fontSize: "14px",
              }}
            >
              {activity.status}{" "}
              <span
                style={{
                  fontWeight: 400,
                  fontSize: "12px",
                  color: "#403C6F",
                }}
              >
                {activity.reason ? `(${activity.reason})` : ""}
              </span>
            </span>
          </div>
        )}
        {activity.timestamp && (
          <div
            style={{
              color: "#1B2F3F",
              fontWeight: 500,
              fontSize: "14px",
              lineHeight: "150%",
              letterSpacing: "0%",
            }}
          >
            {formatDateTimeUtil(activity.timestamp)}
          </div>
        )}
      </div>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      sx={{
        "& .MuiDialog-container": {
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        },
      }}
      slotProps={{
        paper: {
          style: {
            borderRadius: "8px",
            boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.15)",
            backgroundColor: "#F7F8FB",
            width: isMobile ? "95%" : "1000px",
            maxWidth: isMobile ? "95vw" : "50vw",
            margin: isMobile ? "16px" : 0,
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "16px 24px",
        }}
      >
        <span style={{ fontWeight: 600, fontSize: "18px", color: "#1F2937" }}>
          Programs List - {memberName}
        </span>
        <Button
          onClick={onClose}
          size="small"
          sx={{ minWidth: "32px", padding: "4px" }}
        >
          <CloseIcon sx={{ color: "#6B7280" }} />
        </Button>
      </DialogTitle>
      <DialogContent sx={{ padding: "16px 24px" }}>
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            boxShadow: "none",

            borderRadius: "18px",
          }}
        >
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: "#CFCEDA" }}>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    color: "#403C6F",
                    fontSize: "0.8125rem",
                    padding: "9px 16px",
                  }}
                >
                  Programs
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    color: "#403C6F",
                    fontSize: "0.8125rem",
                    padding: "9px 16px",
                  }}
                >
                  Recent Activity
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {programs.map((program, index) => {
                const programName =
                  typeof program === "object" && program !== null
                    ? program.name || JSON.stringify(program)
                    : program;

                const status = program?.status
                  ? program.status.toLowerCase()
                  : getProgramStatus(program);

                const currentStatus = status
                  ? status.toLowerCase().replaceAll("_", " ")
                  : "";
                const statusStyle = {
                  color: iconColors?.color || "#403C6F",
                  bgColor: iconColors?.bgColor || "#F7F8FB",
                  borderColor: iconColors?.borderColor || "#E0E0E0",
                  iconColor: iconColors?.iconColor || "#757575",
                };

                const isBiosimilar = programName
                  .toLowerCase()
                  .includes("biosimilar");
                const isGLP1 =
                  programName.toLowerCase().includes("glp-1") ||
                  programName.toLowerCase().includes("glp1");
                const isEcmP = programName.toUpperCase() === "ECMP";
                const isCopayAssist =
                  programName.toLowerCase() === "copay assist";
                const isPAP = programName.toUpperCase() === "PAP";
                const isInternationalSourcing =
                  programName.toLowerCase() === "international sourcing";
                const isWomensHealth =
                  programName.toLowerCase().includes("women's health") ||
                  programName.toLowerCase().includes("womenshealth") ||
                  programName.toLowerCase().includes("womens health");
                const isDiabetes = programName
                  .toLowerCase()
                  .includes("diabetes");
                const isWeightManagement = programName
                  .toLowerCase()
                  .includes("weight");
                const iconEntry = [
                  [isBiosimilar, biosimilarIcon, "Biosimilars"],
                  [isGLP1, glp1Icon, "GLP-1"],
                  [isEcmP, ecmPIcon, "ECMP"],
                  [isCopayAssist, copayAssistIcon, "CoPay Assist"],
                  [isPAP, papIcon, "PAP"],
                  [
                    isInternationalSourcing,
                    internationalSourcingIcon,
                    "International Sourcing",
                  ],
                  [isWomensHealth, womensHealthIcon, "Women's Health"],
                  [isDiabetes, diabetesIcon, "Diabetes"],
                  [
                    isWeightManagement,
                    weightManagementIcon,
                    "Weight Management",
                  ],
                ].find(([matched]) => matched);
                const statusIcon = iconEntry ? (
                  <img
                    src={iconEntry[1]}
                    alt={iconEntry[2]}
                    style={{
                      width: 18,
                      height: 18,
                      filter: getSvgFilter(statusStyle.iconColor),
                    }}
                  />
                ) : (
                  <TrackChangesIcon
                    style={{
                      color: statusStyle.iconColor,
                      fontSize: "16px",
                    }}
                  />
                );

                const programKey =
                  typeof program === "object" && program?.id
                    ? program.id
                    : `${programName}-${currentStatus}-${index}`;

                return (
                  <TableRow
                    key={programKey}
                    hover
                    sx={{
                      backgroundColor: "#FFFFFF",
                      marginBottom: "6px",
                      "&:not(:last-child)": {
                        marginBottom: "6px",
                      },
                      "& td": {
                        paddingTop: "12px",
                        paddingBottom: "12px",
                        borderBottom: "4px solid #F7F8FB",
                      },
                      "&:hover": {
                        backgroundColor: "#F9FAFB",
                        "& td": {
                          borderBottomColor: "#F9FAFB",
                        },
                      },
                      "&:last-child td": {
                        borderBottom: "8px solid #F7F8FB",
                      },
                    }}
                  >
                    <TableCell sx={{ padding: "8px 16px" }}>
                      <Chip
                        size="small"
                        icon={statusIcon}
                        label={
                          <span
                            style={{
                              whiteSpace: "nowrap",
                              display: "inline-block",
                              color: statusStyle?.color,
                              fontWeight: 500,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              maxWidth:
                                globalThis.window !== undefined &&
                                globalThis.window.innerWidth < 2310
                                  ? 160
                                  : 260,
                            }}
                          >
                            {programName}
                          </span>
                        }
                        sx={{
                          bgcolor: statusStyle?.bgColor,
                          color: statusStyle?.color,
                          border: `1px solid ${statusStyle?.borderColor}`,
                          borderRadius: "25px",
                          height: "27px",
                          justifyContent: "center",
                          "& .MuiChip-icon": {
                            color: statusStyle?.iconColor,
                            margin: 0,
                            marginLeft: "4px",
                            marginRight: "-4px",
                            fontSize: "14px",
                          },
                          "& .MuiChip-label": {
                            fontWeight: 500,
                            paddingLeft: "8px",
                            overflow: "hidden",
                            textAlign: "center",
                            display: "flex",
                            justifyContent: "center",
                            minWidth: 0,
                            maxWidth: "100%",
                          },
                          padding: "4px",
                        }}
                      />
                    </TableCell>
                    <TableCell
                      sx={{
                        padding: "12px 16px",
                        borderBottom: "1px solid #E5E7EB",
                      }}
                    >
                      {formatActivityText(program)}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
    </Dialog>
  );
});

const getSvgFilter = (color) => {
  if (
    !color ||
    typeof color !== "string" ||
    !/^#([0-9A-F]{3}){1,2}$/i.test(color)
  ) {
    color = "#808080";
  }

  const hexToRgb = (hex) => {
    try {
      const hexValue =
        hex.length === 4
          ? "#" + hex[1] + hex[1] + hex[2] + hex[2] + hex[3] + hex[3]
          : hex;

      const r = Number.parseInt(hexValue.slice(1, 3), 16);
      const g = Number.parseInt(hexValue.slice(3, 5), 16);
      const b = Number.parseInt(hexValue.slice(5, 7), 16);
      return [r, g, b];
    } catch (e) {
      // Hex color parsing failed; return default gray RGB values
      console.debug("Hex color parsing failed:", e);
      return [128, 128, 128];
    }
  };

  try {
    const [r, g, b] = hexToRgb(color);
    return `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='recolor' color-interpolation-filters='sRGB'%3E%3CfeColorMatrix type='matrix' values='0 0 0 0 ${
      r / 255
    } 0 0 0 0 ${g / 255} 0 0 0 0 ${
      b / 255
    } 0 0 0 1 0'/%3E%3C/filter%3E%3C/svg%3E#recolor")`;
  } catch (e) {
    // SVG filter generation failed; return fallback value
    console.debug("SVG filter generation failed:", e);
    return "none";
  }
};

const normalizeStatus = (status) => {
  if (!status) return "eligible";
  return status.toLowerCase().replaceAll("_", " ");
};
const getProgramStatusStyle = (status, program) => {
  return {
    color: iconColors?.color || "#403C6F",
    bgColor: iconColors?.bgColor || "#F7F8FB",
    borderColor: iconColors?.borderColor || "#E0E0E0",
    iconColor: iconColors?.iconColor || "#757575",
  };
};

const classifyProgramType = (name) => {
  if (!name) return null;
  const n = String(name).toLowerCase();
  if (n.includes("biosimilar")) return "biosimilar";
  if (n.includes("glp-1") || n.includes("glp1")) return "glp1";
  if (n === "ecmp") return "ecmp";
  if (n === "copay assist" || n.includes("copay")) return "copayassist";
  if (n === "pap") return "pap";
  if (n === "international sourcing") return "intsourcing";
  if (
    n.includes("women's health") ||
    n.includes("womenshealth") ||
    n.includes("womens health")
  )
    return "womenshealth";
  if (n.includes("diabetes")) return "diabetes";
  if (n.includes("weight")) return "weightmanagement";
  return null;
};

const buildProgramTypeIcon = (type, style) => {
  const iconProps = {
    ariaHidden: true,
    style: {
      width: "16px",
      height: "16px",
      marginRight: "1px",
      filter: getSvgFilter(style?.iconColor),
    },
  };
  switch (type) {
    case "biosimilar":
      return <img src={biosimilarIcon} alt="" {...iconProps} />;
    case "glp1":
      return <img src={glp1Icon} alt="" {...iconProps} />;
    case "ecmp":
      return <img src={ecmPIcon} alt="" {...iconProps} />;
    case "copayassist":
      return <img src={copayAssistIcon} alt="" {...iconProps} />;
    case "pap":
      return <img src={papIcon} alt="" {...iconProps} />;
    case "intsourcing":
      return <img src={internationalSourcingIcon} alt="" {...iconProps} />;
    case "womenshealth":
      return <img src={womensHealthIcon} alt="" {...iconProps} />;
    case "diabetes":
      return <img src={diabetesIcon} alt="" {...iconProps} />;
    case "weightmanagement":
    case "weight":
      return <img src={weightManagementIcon} alt="" {...iconProps} />;
    default:
      return <></>;
  }
};

const buildStatusStateIcon = (status, style) => {
  const common = { fontSize: "16px", color: style?.color, minWidth: "16px" };
  const normalized = normalizeStatus(status);
  if (normalized === "enrolled" || normalized === "resolved")
    return <CheckCircleIcon style={common} />;
  if (normalized === "eligible") return <TrackChangesIcon style={common} />;
  if (normalized === "declined" || normalized === "not resolved")
    return <CancelIcon style={common} />;
  return <></>;
};

ProgramsPopup.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  memberName: PropTypes.string,
  programs: PropTypes.array,
};

const ProgramChip = React.memo(({ program, onClick, clickable = false }) => {
  const programName =
    typeof program === "object" && program?.name ? program.name : program;
  const rawStatus =
    typeof program === "object" && program?.status
      ? program.status
      : getProgramStatus(program);
  const statusStyle = getProgramStatusStyle(rawStatus, program);
  const programType = classifyProgramType(programName);
  const normalizedStatus = normalizeStatus(rawStatus);
  const hasStatusIcon =
    Boolean(programType) ||
    ["enrolled", "resolved", "eligible", "declined", "not resolved"].includes(
      normalizedStatus,
    );
  const statusIcon = programType
    ? buildProgramTypeIcon(programType, statusStyle)
    : buildStatusStateIcon(rawStatus, statusStyle);

  const handleClick = (e) => {
    if (!clickable) return;
    e.stopPropagation();
    onClick?.();
  };

  return (
    <Tooltip
      title={programName || ""}
      placement="bottom-start"
      arrow
      describeChild
      disableInteractive
      slotProps={{
        popper: {
          sx: {
            "& .MuiTooltip-tooltip": {
              backgroundColor: "#343435",
              color: "#FFFFFF",
              margin: 0,
              fontSize: "12px",
              lineHeight: 1.3,
            },
            "& .MuiTooltip-arrow": {
              color: "#343435",
              margin: 0,
              left: "50% !important",
              transform: "translateX(-50%) !important",
            },
          },
        },
        tooltip: {
          sx: {
            margin: 0,
            marginTop: "0px !important",
            padding: "4px 8px",
          },
        },
      }}
    >
      <Chip
        size="small"
        onClick={clickable ? handleClick : undefined}
        label={
          <span
            style={{
              whiteSpace: "nowrap",
              display: "inline-block",
              color: statusStyle.color,
              fontWeight: 500,
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth:
                globalThis.window !== undefined &&
                globalThis.window.innerWidth < 2310
                  ? 60
                  : 200,
            }}
          >
            {programName}
          </span>
        }
        icon={hasStatusIcon ? statusIcon : undefined}
        sx={{
          bgcolor: statusStyle.bgColor,
          color: statusStyle.color,
          border: `1px solid ${statusStyle.borderColor}`,
          borderRadius: "25px",
          height: "27px",
          justifyContent: "center",
          cursor: clickable ? "pointer" : "default",
          "& .MuiChip-icon": {
            color: statusStyle.iconColor,
            margin: 0,
            marginLeft: "4px",
            marginRight: "-4px",
            fontSize: "14px",
          },
          "& .MuiChip-label": {
            fontWeight: 500,
            paddingLeft: "8px",
            overflow: "hidden",
            textAlign: "center",
            display: "flex",
            justifyContent: "center",
            minWidth: 0,
            maxWidth: "100%",
          },
          padding: "4px",
          ...(clickable && { "&:hover": { opacity: 0.9 } }),
        }}
      />
    </Tooltip>
  );
});

ProgramChip.propTypes = {
  program: PropTypes.object,
  onClick: PropTypes.func,
  clickable: PropTypes.bool,
};

const ProgramsCell = React.memo(({ row }) => {
  const programs = row.original.programs || [];
  const memberName = row.original.member?.name || row.original.member || "";
  const [open, setOpen] = React.useState(false);
  const showMoreChip = programs.length > 3;
  const displayCount = 3;
  if (programs.length === 0) {
    return <div style={{ fontWeight: 500, fontSize: "14px" }}>N/A</div>;
  }
  const displayPrograms = showMoreChip
    ? programs.slice(0, displayCount)
    : programs;

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <div
      style={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        verticalAlign: "middle",
        height:
          (programs.length < 3 &&
            globalThis.location.pathname.includes("/member-outreach")) ||
          globalThis.location.pathname.includes("/provider-outreach") ||
          globalThis.location.pathname.includes("/pharmacy-outreach")
            ? "70px"
            : "100%",
        padding: "4px 0 4px 0",
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          width: "100%",
          gap: "4px",
        }}
      >
        {displayPrograms.map((program, index) => {
          const isLast = index === displayCount - 1 && showMoreChip;
          let programKey;

          if (typeof program === "object" && program?.id) {
            programKey = program.id;
          } else if (typeof program === "string") {
            programKey = program;
          } else {
            programKey = `program-${index}`;
          }

          return (
            <div
              key={programKey}
              style={{
                display: "flex",
                alignItems: "center",
                minHeight: "24px",
                width: "100%",
              }}
            >
              <ProgramChip
                program={program}
                onClick={handleClickOpen}
                style={{
                  justifyContent: "flex-start",
                }}
              />
              {isLast && showMoreChip && (
                <Chip
                  label={`+ ${programs.length - displayCount}`}
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClickOpen();
                  }}
                  sx={{
                    bgcolor: "#ECECF1",
                    color: "#403C6F",
                    fontSize: "0.75rem",
                    marginLeft: "4px",
                  }}
                />
              )}
            </div>
          );
        })}
      </div>
      <ProgramsPopup
        open={open}
        onClose={handleClose}
        memberName={memberName}
        programs={programs}
        row={row}
      />
    </div>
  );
});

ProgramsCell.propTypes = {
  row: PropTypes.object,
};

// Programs Column
export const programsColumn = {
  header: createHeaderWithSort("Programs", true),
  accessorKey: "programs",
  size: isNarrowViewport() ? 130 : 170,
  minSize: isNarrowViewport() ? 110 : 150,
  maxSize: isNarrowViewport() ? 170 : 240,
  enableSorting: true,
  id: "program",
  sortingFn: (rowA, rowB) => {
    const a = rowA.original.programs?.[0]?.name || "";
    const b = rowB.original.programs?.[0]?.name || "";
    return a.localeCompare(b);
  },
  cell: ProgramsCell,
};

export const getRecentActivityColumn = (enableSorting = true) => ({
  sortDescFirst: true,
  header: createHeaderWithSort("Recent Activity", enableSorting),

  // Normalize empty string to undefined
  accessorFn: (row) => row.recentActivity?.timestamp || undefined,
  id: "recentActivity",

  size: isNarrowViewport() ? 24 : 110,
  minSize: isNarrowViewport() ? 16 : 90,
  maxSize: isNarrowViewport() ? 36 : 150,

  meta: {
    headerPadding: isNarrowViewport() ? "2px 4px" : "6px 10px",
    cellPadding: isNarrowViewport() ? "4px 4px" : "6px 10px",
  },

  enableSorting,

  sortUndefined: "last", // empty always at bottom

  sortingFn: (rowA, rowB, columnId) => {
    const a = rowA.getValue(columnId);
    const b = rowB.getValue(columnId);
    if (!a || !b) return 0;
    return new Date(a).getTime() - new Date(b).getTime();
  },

  cell: ({ row }) => {
    const activity = row.original.recentActivity || {};

    if (!activity.status && !activity.timestamp) {
      return <span>N/A</span>;
    }
    const narrow = isNarrowViewport();
    const maxCellWidth = narrow ? "100px" : "140px";

    let statusContent = null;
    if (narrow) {
      if (activity.reason) {
        statusContent = (
          <div
            style={{
              maxWidth: "100%",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            <span
              style={{
                fontWeight: 600,
                fontSize: "14px",
                color: getRecentActivityColor(activity),
                whiteSpace: "nowrap",
                flex: "0 0 auto",
              }}
            >
              {activity.status}
            </span>
            <span
              style={{
                fontWeight: 400,
                fontSize: "12px",
                color: "#403C6F",
                maxWidth: "100%",
                whiteSpace: "nowrap",
                overflow: "hidden",
              }}
            >
              {` (${activity.reason})`}
            </span>
          </div>
        );
      } else {
        statusContent = (
          <div
            style={{
              color: getRecentActivityColor(activity),
              fontWeight: 600,
              fontSize: "14px",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: "100%",
            }}
          >
            {activity.status}
          </div>
        );
      }
    } else {
      statusContent = (
        <div
          style={{
            color: getRecentActivityColor(activity),
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
            gap: 4,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            width: "100%",
          }}
        >
          <span
            style={{
              fontWeight: 600,
              fontSize: "14px",
              whiteSpace: "nowrap",
              flex: "0 0 auto",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {activity.status}
          </span>
          {activity.reason && (
            <span
              style={{
                fontWeight: 400,
                fontSize: "12px",
                color: "#403C6F",
                flex: "1 1 auto",
                minWidth: 0,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {` (${activity.reason})`}
            </span>
          )}
        </div>
      );
    }

    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 4,
          maxWidth: maxCellWidth,
        }}
      >
        {activity.status && (
          <Tooltip
            title={
              activity.reason
                ? `${activity.status} (${activity.reason})`
                : activity.status
            }
            placement="bottom-start"
            arrow
            disableInteractive
            slotProps={{
              popper: {
                sx: {
                  "& .MuiTooltip-tooltip": {
                    backgroundColor: "#343435",
                    color: "#FFFFFF",
                    fontSize: "12px",
                  },
                  "& .MuiTooltip-arrow": {
                    color: "#343435",
                  },
                },
              },
            }}
          >
            {statusContent}
          </Tooltip>
        )}

        {activity.timestamp && (
          <Tooltip
            title={formatDateTimeUtil(activity.timestamp)}
            placement="bottom-start"
            arrow
            disableInteractive
            slotProps={{
              popper: {
                sx: {
                  "& .MuiTooltip-tooltip": {
                    backgroundColor: "#343435",
                    color: "#FFFFFF",
                    fontSize: "12px",
                  },
                  "& .MuiTooltip-arrow": {
                    color: "#343435",
                  },
                },
              },
            }}
          >
            <div
              style={{
                color: "#1B2F3F",
                fontSize: "14px",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {formatDateTimeUtil(activity.timestamp)}
            </div>
          </Tooltip>
        )}

        {globalThis.location.pathname.includes("/monitoring") &&
          activity.role && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 4,
                color: "#403C6F",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              <img src={RoleIcon} alt={activity.role} width={16} height={16} />
              {activity.role}
            </div>
          )}
      </div>
    );
  },
});

export const getlatestDateAddedColumn = (enableSorting = true) => ({
  sortDescFirst: true,
  header: createHeaderWithSort("Halo Date", enableSorting),
  accessorFn: (row) =>
    row.latestDateAdded || row.manualTask?.latestDateAdded || undefined,

  id: "latestDateAdded",
  size: 150,

  enableSorting,
  sortUndefined: "last",
  sortingFn: (rowA, rowB, columnId) => {
    const a = rowA.getValue(columnId);
    const b = rowB.getValue(columnId);

    if (!a || !b) return 0;

    return new Date(a).getTime() - new Date(b).getTime(); // ASC base
  },

  cell: ({ row }) => {
    let latestDateAdded = null;

    if (row.original.latestDateAdded) {
      latestDateAdded = new Date(row.original.latestDateAdded);
    } else if (row.original.manualTask?.latestDateAdded) {
      latestDateAdded = new Date(row.original.manualTask.latestDateAdded);
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const displayDate = latestDateAdded
      ? latestDateAdded.toLocaleDateString("en-US", {
          month: "2-digit",
          day: "numeric",
          year: "numeric",
          timeZone: "UTC",
        })
      : "N/A";
    return (
      <Tooltip
        title={displayDate}
        placement="bottom-start"
        arrow
        describeChild
        disableInteractive
        slotProps={{
          popper: {
            sx: {
              "& .MuiTooltip-tooltip": {
                backgroundColor: "#343435",
                color: "#FFFFFF",
                margin: 0,
                fontSize: "12px",
                lineHeight: 1.3,
              },
              "& .MuiTooltip-arrow": {
                color: "#343435",
                margin: 0,
                left: "50% !important",
                transform: "translateX(-50%) !important",
              },
            },
          },
          tooltip: {
            sx: {
              margin: 0,
              marginTop: "0px !important",
              padding: "4px 8px",
            },
          },
        }}
      >
        <div
          style={{
            color: "#1B2F3F",
            fontWeight: 500,
            fontSize: "16px",
            lineHeight: "150%",
            letterSpacing: "0%",
          }}
        >
          {displayDate}
        </div>
      </Tooltip>
    );
  },
});

export const getOutreachColumn = (enableSorting = true) => ({
  header: createHeaderWithSort("Outreach Reason", enableSorting),
  id: "outreachReason",

  accessorFn: (row) => row?.manualTask?.outreachReason || "N/A",
  enableSorting,
  sortDescFirst: true,
  size: 150,
  ...(enableSorting && {
    sortingFn: (rowA, rowB) => {
      const a = rowA.original?.manualTask?.outreachReason || "";
      const b = rowB.original?.manualTask?.outreachReason || "";

      return a.localeCompare(b);
    },
  }),
  cell: ({ row }) => {
    const outreachRaw = row.original?.manualTask?.outreachReason || "N/A";

    const formatOutreachReason = (raw = "") => {
      if (!raw || raw === "N/A") return "N/A";
      const trimmed = String(raw).trim();
      const upper = trimmed.toUpperCase();
      const LOOKUP = {
        PROVIDER_FOLLOW_UP: "Provider Follow-up",
        MEMBER_FOLLOW_UP: "Member Follow-up",
        PROVIDER_CONSULTATION: "Provider Consultation",
        PROVIDER_EDUCATION: "Provider Education",
        DOSAGE_OPTIMISATION: "Dosing Optimization",
        REQUEST_PRESCRIPTION: "Request Prescription",
        CONFIRM_RX_RECEIVED: "Confirm Rx Received",
        PHARMACY_CONSULTATION: "Pharmacy Consultation",
        SCHEDULE_CALLBACK: "Schedule Callback",
      };

      if (LOOKUP[upper]) return LOOKUP[upper];

      const underscored = upper.replaceAll(/\s+/g, "_");
      if (LOOKUP[underscored]) return LOOKUP[underscored];

      const title = trimmed
        .toLowerCase()
        .split(/\s+/)
        .map((w) =>
          w === "rx" ? "Rx" : w.charAt(0).toUpperCase() + w.slice(1),
        )
        .join(" ")
        .replace(/Follow Up/i, "Follow-up");
      return title;
    };
    const outreach = formatOutreachReason(outreachRaw);
    return (
      <div
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "4px",
          padding: "4px 12px",
          borderRadius: "20px",
          backgroundColor: "#ECECF1",
          color: "#403C6F",
          fontSize: "14px",
          fontWeight: 500,
          height: "27px",
          lineHeight: "16px",
          maxWidth: "240px",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {outreach}
      </div>
    );
  },
});

export const actionsColumn = (onQuickView) => ({
  id: "actions",

  header: () => null,
  enableSorting: false,
  cell: (info) => (
    <Tooltip
      title="View details"
      placement="bottom"
      arrow
      describeChild
      disableInteractive
    >
      <Button
        aria-label="View member details"
        onClick={(e) => {
          e.stopPropagation();
          onQuickView(info.row.original);
        }}
        sx={{
          textTransform: "none",
          fontWeight: 500,
          color: "#403C6F",
          backgroundColor: "transparent",
          border: "0.92px solid #E9E9E8",
          borderRadius: "6px",
          py: 0.5,
          minWidth: "40px",
          height: "28px",
          fontSize: "0.8125rem",
          boxShadow: "none",
          "&:hover": {
            backgroundColor: "#F9F9F9",
            boxShadow: "none",
            cursor: "pointer",
          },
          "&:active": {
            transform: "scale(0.98)",
          },
          transition: "all 0.2s ease-in-out",
        }}
      >
        <img src={viewIcon} alt="View" style={{ width: 18, height: 18 }} />
      </Button>
    </Tooltip>
  ),
});

export const getRejectStatusColumn = (enableSorting = true) => ({
  header: createHeaderWithSort("Reject status", enableSorting),
  accessorKey: "rejectStatus",
  id: "rejectStatus",
  size: 150,
  enableSorting,
  cell: ({ row }) => {
    const paActionsRaw = row.original.paActions;
    const toArray = (value) => {
      if (Array.isArray(value)) return value;
      return value ? [value] : [];
    };
    const paActions = toArray(paActionsRaw);

    const rejectionCodesRaw = row.original.rejectionCodes;
    const rejectionCodes = toArray(rejectionCodesRaw);

    const cleanRejectionCodes = rejectionCodes.filter(
      (c) => c && String(c).trim() !== "" && String(c).toUpperCase() !== "N/A",
    );

    const cleanPaActions = paActions.filter(
      (a) => a && String(a).trim() !== "" && String(a).toUpperCase() !== "N/A",
    );

    if (cleanRejectionCodes.length === 0 && cleanPaActions.length === 0) {
      return <span style={{ color: "#7F7C9F", fontWeight: 500 }}>—</span>;
    }

    const approvedTheme = {
      border: "1.5px solid #45B184",
      backgroundColor: "#E8F5E9",
      color: "#2E7D32",
    };

    const rejectedTheme = {
      border: "1.5px solid #D13D53",
      backgroundColor: "#FFF5F6",
      color: "#D13D53",
    };

    const isPaApproved = (action) => {
      const str = String(action || "")
        .toLowerCase()
        .trim();
      return (
        str.includes("approved") &&
        !str.includes("not") &&
        !str.includes("reject") &&
        !str.includes("denied")
      );
    };

    const pills = [
      ...cleanRejectionCodes.map((code) => ({
        text: code,
        theme: rejectedTheme,
      })),
      ...cleanPaActions.map((action) => ({
        text: action,
        theme: isPaApproved(action) ? approvedTheme : rejectedTheme,
      })),
    ];

    return (
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "8px",
          alignItems: "center",
        }}
      >
        {pills.map((pill, idx) => (
          <div
            key={pill.text + idx}
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              border: pill.theme.border,
              backgroundColor: pill.theme.backgroundColor,
              borderRadius: "40px",
              padding: "2px 12px",
              fontSize: "12px",
              fontWeight: 700,
              color: pill.theme.color,
              textTransform: "uppercase",
              lineHeight: 1.2,
              whiteSpace: "nowrap",
            }}
          >
            {pill.text}
          </div>
        ))}
      </div>
    );
  },
});

export const rejectStatusColumn = getRejectStatusColumn(true);

// Export all columns as an array for easy use
export const getColumns = (
  onQuickView,
  assigneeVisitKey,
  enableSorting = true,
  showPHI = false,
  isRejectedClaimsOn = false,
  isBulkModeActive = false,
) => {
  // Pass showPHI into memberColumn and familyColumn via meta so cell renderers can access it
  const memberCol = {
    ...memberColumn,
    meta: { ...memberColumn.meta, showPHI },
  };
  const role = getUserPrimaryRole();
  const isL6L7 = role === "L6" || role === "L7";
  const canSeePriority =
    ["L9", "L5", "L6", "L7"].includes(role) && !isRejectedClaimsOn;
  return [
    expanderColumn,
    memberCol,
    ...(isRejectedClaimsOn && isL6L7
      ? [getRejectStatusColumn(enableSorting)]
      : []),
    ...(canSeePriority ? [priorityColumn] : []),
    cagColumn,
    statusColumn,
    programsColumn,
    assigneeVisitKey === undefined
      ? assigneeColumn
      : createAssigneeColumn(assigneeVisitKey, isBulkModeActive),

    getRecentActivityColumn(enableSorting),
    getlatestDateAddedColumn(enableSorting),
    actionsColumn(onQuickView),
  ];
};

export const getColumnsWithSelectiveSorting = (
  onQuickView,
  sortingConfig = {},
) => {
  const {
    member = true,
    provider = true,
    pharmacy = true,
    programs = true,
    recentActivity = true,
    latestDateAdded = true,
  } = sortingConfig;

  const nonSortableMemberColumn = {
    ...memberColumn,
    enableSorting: false,
    header: createHeaderWithSort("Member Details", false),
    sortingFn: undefined,
  };

  const nonSortableProviderColumn = {
    ...providerColumn,
    enableSorting: false,
    header: createHeaderWithSort("Provider", false),
    sortingFn: undefined,
  };

  const nonSortablePharmacyColumn = {
    ...pharmacyColumn,
    enableSorting: false,
    header: createHeaderWithSort("Pharmacy", false),
    sortingFn: undefined,
  };

  const nonSortableProgramsColumn = {
    ...programsColumn,
    enableSorting: false,
    header: createHeaderWithSort("Programs", false),
    sortingFn: undefined,
  };

  const isL9 = getUserPrimaryRole() === "L9";

  return [
    member ? memberColumn : nonSortableMemberColumn,
    ...(isL9 ? [priorityColumn] : []),
    provider ? providerColumn : nonSortableProviderColumn,
    pharmacy ? pharmacyColumn : nonSortablePharmacyColumn,
    programs ? programsColumn : nonSortableProgramsColumn,
    getRecentActivityColumn(recentActivity),
    getlatestDateAddedColumn(latestDateAdded),
    actionsColumn(onQuickView),
  ];
};

export const getOpportunityColumns = (onQuickView, enableSorting = true) => {
  const isL9 = getUserPrimaryRole() === "L9";
  return [
    memberColumn,
    ...(isL9 ? [priorityColumn] : []),
    programsColumn,
    getRecentActivityColumn(enableSorting),
    getlatestDateAddedColumn(enableSorting),
    actionsColumn(onQuickView),
  ];
};
export const checkboxColumn = {
  id: "selection",
  // meta.onToggleAll(checked) lets a page take over "select all" (e.g. to
  // load every remaining page first) instead of only selecting what's loaded.
  header: ({ table, column }) => {
    const onToggleAll = column?.columnDef?.meta?.onToggleAll;
    const isAllSelected = table.getIsAllPageRowsSelected();
    const isSomeSelected = table.getIsSomePageRowsSelected();
    const isAnySelected = isAllSelected || isSomeSelected;
    return (
      <Checkbox
        checked={isAllSelected}
        onChange={(event) => {
          if (onToggleAll) {
            onToggleAll(!isAnySelected);
          } else if (isSomeSelected) {
            table.toggleAllPageRowsSelected(false);
          } else {
            table.getToggleAllPageRowsSelectedHandler()(event);
          }
        }}
        indeterminate={isSomeSelected}
        disabled={Boolean(column?.columnDef?.meta?.isSelectingAll)}
      />
    );
  },
  // meta.selectionLimitReached disables every unchecked checkbox once the
  // page-imposed selection cap is hit (already-checked rows stay clickable so
  // they can be unchecked).
  cell: ({ row, column }) =>
    row.depth > 0 ? null : (
      <Checkbox
        checked={row.getIsSelected()}
        disabled={
          !row.getIsSelected() &&
          Boolean(column?.columnDef?.meta?.selectionLimitReached)
        }
        onChange={row.getToggleSelectedHandler()}
        onClick={(e) => e.stopPropagation()}
        sx={{
          "&.Mui-checked": {
            color: "#403C6F",
            "& .MuiSvgIcon-root": {
              fill: "#403C6F",
            },
          },
        }}
      />
    ),
  size: 50,
  enableSorting: false,
};
export const attemptsColumn = {
  id: "attempts",
  header: createHeaderWithSort(
    <Tooltip title="No. of Attempts" arrow>
      <span>No. of Attempts</span>
    </Tooltip>,
    true,
  ),

  accessorFn: (row) => row?.manualTask?.noOfAttempts ?? 0,
  enableSorting: true,
  sortingFn: (rowA, rowB) => {
    const a = rowA.original?.manualTask?.noOfAttempts ?? 0;
    const b = rowB.original?.manualTask?.noOfAttempts ?? 0;
    return a - b;
  },
  cell: ({ row }) => (
    <div
      style={{
        textAlign: "left",
        whiteSpace: "nowrap",
        color: "#403C6F",
        overflow: "hidden",
        textOverflow: "ellipsis",
        width: "100%",
        display: "inline-block",
      }}
    >
      {row.original?.manualTask?.noOfAttempts ?? 0}
    </div>
  ),

  size: 60,
  minSize: 60,
  maxSize: 80,
};
const TaskNotePopup = ({ open, onClose, note, title = "Task Note" }) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            borderRadius: "8px",
            maxWidth: "600px",
            backgroundColor: "#F7F8FB",
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography
          variant="h6"
          sx={{ fontWeight: 600, fontSize: "18px", color: "#1F2937" }}
        >
          {title}
        </Typography>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: "#6B7280",
            "&:hover": {
              backgroundColor: "rgba(107, 114, 128, 0.1)",
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent
        sx={{
          border: "1px solid #E9E9E8",
          backgroundColor: "#FFFFFF",
          marginLeft: "22px",
          marginRight: "22px",
          borderRadius: "8px",
          marginBottom: "20px",
          paddingBottom: "16px",
          paddingTop: "16px !important",
        }}
      >
        <Typography
          sx={{
            color: "#403C6F",
            fontSize: "14px",
            lineHeight: "1.6",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
        >
          {note || "No note available"}
        </Typography>
      </DialogContent>
    </Dialog>
  );
};

TaskNotePopup.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  note: PropTypes.string,
  title: PropTypes.string,
};

const TaskNoteCell = ({ note, recipientName }) => {
  const [showPopup, setShowPopup] = useState(false);

  const maxLength = 100;
  const needsTruncation = note?.length > maxLength;
  const displayText = needsTruncation
    ? note.substring(0, maxLength) + "..."
    : note;

  const handleReadMore = (e) => {
    e.stopPropagation();
    setShowPopup(true);
  };

  const handleClosePopup = () => {
    setShowPopup(false);
  };

  const popupTitle = recipientName
    ? `Task Note (${recipientName})`
    : "Task Note";

  return (
    <div style={{ width: "100%", minWidth: "200px" }}>
      <div
        style={{
          color: "#403C6F",
          lineHeight: "1.5",
          fontSize: "14px",
          display: "-webkit-box",
          WebkitLineClamp: needsTruncation ? 2 : "none",
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
          wordWrap: "break-word",
          hyphens: "auto",
          whiteSpace: "pre-line",
          maxHeight: needsTruncation ? "2.8em" : "none",
          width: "100%",
        }}
      >
        {displayText}
      </div>
      {needsTruncation && (
        <Button
          onClick={handleReadMore}
          sx={{
            padding: "2px 0",
            minWidth: "auto",
            textTransform: "none",
            fontSize: "12px",
            color: "#403C6F",
            textDecoration: "underline",
            "&:hover": {
              backgroundColor: "transparent",
              textDecoration: "underline",
              opacity: 0.8,
            },
            marginTop: "4px",
          }}
        >
          Read more
        </Button>
      )}
      <TaskNotePopup
        open={showPopup}
        onClose={handleClosePopup}
        note={note}
        title={popupTitle}
      />
    </div>
  );
};

TaskNoteCell.propTypes = {
  note: PropTypes.string,
  recipientName: PropTypes.string,
};

export const taskNoteColumn = {
  id: "taskNote",
  header: createHeaderWithSort("Task Note", true),
  accessorKey: "taskNote",

  sortingFn: (rowA, rowB) => {
    const a = rowA.original.taskNote || "";
    const b = rowB.original.taskNote || "";
    return a.localeCompare(b);
  },
  cell: ({ row }) => {
    const manualTask = row.original.manualTask || {};
    const note = manualTask.taskNote || "";
    const recipientName =
      row.original.recipient?.name || row.original.member?.name || "Unknown";
    return <TaskNoteCell note={note} recipientName={recipientName} />;
  },
  size: 1000,
  minSize: 1000,
  maxSize: 1000,
  enableSorting: true,
};

const editOpportunityColumn = (onEditOpportunity) => ({
  id: "edit",
  header: () => null,
  enableSorting: false,
  size: 50,
  cell: ({ row }) => (
    <IconButton
      aria-label="Edit task"
      size="small"
      onClick={(e) => {
        e.stopPropagation();
        if (onEditOpportunity) onEditOpportunity(row.original);
      }}
      sx={{
        width: 32,
        height: 32,
        borderRadius: "6px",

        boxShadow: "none",
        "&:hover": {
          backgroundColor: "rgba(232,232,233,0.2)",
        },
        "& .MuiSvgIcon-root": {
          fontSize: 20,
          color: "#6E6D92",
        },
      }}
    >
      <EditOutlinedIcon />
    </IconButton>
  ),
});

export const getOpportunityColumnsBase = (
  onQuickView,
  onEditOpportunity,
  enableSorting = true,
) => {
  const isL9 = getUserPrimaryRole() === "L9";
  return [
    checkboxColumn,
    editOpportunityColumn(onEditOpportunity),
    { ...memberColumn, id: "member" },
    ...(isL9 ? [priorityColumn] : []),
    { ...attemptsColumn, id: "attempts" },
    { ...taskNoteColumn, id: "taskNote" },
    getOutreachColumn(enableSorting),
    getRecentActivityColumn(enableSorting),
    getlatestDateAddedColumn(enableSorting),
    actionsColumn(onQuickView),
  ];
};

export const getPharmacyOpportunityColumns = (
  onQuickView,
  onEditOpportunity,
  enableSorting = true,
) => getOpportunityColumnsBase(onQuickView, onEditOpportunity, enableSorting);

export const getProviderOpportunityColumns = (
  onQuickView,
  onEditOpportunity,
  enableSorting = true,
) => getOpportunityColumnsBase(onQuickView, onEditOpportunity, enableSorting);

export const pharmacyMemberColumn = {
  header: createHeaderWithSort("Member Details", true),
  accessorKey: "member",
  id: "memberDetails",
  size: 140,
  sortingFn: (rowA, rowB) => {
    const a = rowA.original.member?.name || "";
    const b = rowB.original.member?.name || "";
    return a.localeCompare(b);
  },
  enableSorting: true,
  cell: ({ row }) => {
    const member = row.original.member || row.original;

    const memberName = member.name || member.member || "";
    return (
      <div
        style={{
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          fontWeight: 500,
          fontSize: "14px",
          lineHeight: "150%",
          letterSpacing: "0%",
          minWidth: "140px",
          maxWidth: "140px",
          width: "140px",
        }}
      >
        <div
          style={{
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            fontWeight: 500,
            fontSize: "16px",
            lineHeight: "150%",
            letterSpacing: "0%",
          }}
        >
          <Tooltip
            title={memberName || ""}
            placement="bottom-start"
            arrow
            describeChild
            disableInteractive
            slotProps={{
              popper: {
                sx: {
                  "& .MuiTooltip-tooltip": {
                    backgroundColor: "#343435",
                    color: "#FFFFFF",
                    margin: 0,
                  },
                  "& .MuiTooltip-arrow": {
                    color: "#343435",
                    margin: 0,
                    left: "50% !important",
                    transform: "translateX(-50%) !important",
                  },
                },
              },
              tooltip: {
                sx: {
                  margin: 0,
                  marginTop: "0px !important",
                  padding: "4px 8px",
                },
              },
            }}
          >
            <div
              style={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                fontWeight: 500,
                fontSize: "16px",
                lineHeight: "150%",
                letterSpacing: "0%",
              }}
            >
              {capitalizeFirstLetter(memberName)}
            </div>
          </Tooltip>
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            fontWeight: 500,
            fontSize: "10px",
            lineHeight: "150%",
            letterSpacing: "0%",
            color: "#403C6F",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <Tooltip title="Timezone" arrow>
              <WatchLaterRoundedIcon
                style={{ fontSize: 14, color: "#1B2F3F" }}
              />
            </Tooltip>
            <span>{member.timeZone || "N/A"}</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <Tooltip title="Language" arrow>
              <LanguageRoundedIcon style={{ fontSize: 14, color: "#1B2F3F" }} />
            </Tooltip>
            <span>
              {member.language?.toUpperCase() === "N/A"
                ? "N/A"
                : member.language || "N/A"}
            </span>
          </div>
        </div>
      </div>
    );
  },
};

export const pharmacyDetailsColumn = {
  header: createHeaderWithSort("Pharmacy Details", true),
  accessorKey: "pharmacy",
  id: "pharmacyDetails",
  size: 120,
  sortingFn: (rowA, rowB) => {
    const a = rowA.original.pharmacy?.name || "";
    const b = rowB.original.pharmacy?.name || "";
    return a.localeCompare(b);
  },
  cell: ({ row }) => {
    const pharmacy =
      row.original.pharmacy || row.original.pharmacyDetails || row.original;

    const pharmacyName =
      typeof pharmacy === "object" && pharmacy !== null
        ? pharmacy.name || pharmacy.pharmacy || ""
        : pharmacy;
    const pharmacyPhone =
      typeof pharmacy === "object" && pharmacy !== null
        ? pharmacy.contact || pharmacy.pharmacyPhone || ""
        : "";
    const onQuickView = row.original.onQuickView;
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 4,
          minWidth: "120px",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            flexWrap: "nowrap",
            width: "100%",
          }}
        >
          <Tooltip
            title={capitalizeFirstLetter(pharmacyName) || ""}
            placement="bottom-start"
            arrow
            describeChild
            disableInteractive
            slotProps={{
              popper: {
                sx: {
                  "& .MuiTooltip-tooltip": {
                    backgroundColor: "#343435",
                    color: "#FFFFFF",
                    margin: 0,
                  },
                  "& .MuiTooltip-arrow": {
                    color: "#343435",
                    margin: 0,
                    left: "50% !important",
                    transform: "translateX(-50%) !important",
                  },
                },
              },
              tooltip: {
                sx: {
                  margin: 0,
                  marginTop: "0px !important",
                  padding: "4px 8px",
                },
              },
            }}
          >
            <Link
              to={`/pharmacyProfile/${row.original.key}`}
              title={capitalizeFirstLetter(pharmacyName)}
              style={{
                fontWeight: 600,
                fontStyle: "Bold",
                fontSize: "17px",
                color: "#4b3f72",
                flexDirection: "column",
                textDecoration: "underline",
                textDecorationStyle: "solid",
                textDecorationThickness: 1,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                textDecorationSkipInk: "auto",
                width: "150px",
                minWidth: "150px",
                textUnderlineOffset: 3,
                cursor: "pointer",
                flex: 1,
                display: "inline-block",
                marginTop: "4px",
              }}
            >
              {capitalizeFirstLetter(pharmacyName)}
            </Link>
          </Tooltip>
          {onQuickView && (
            <Button
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                onQuickView(row.original);
              }}
              sx={{
                textTransform: "none",
                fontWeight: 500,
                color: "#403C6F",
                backgroundColor: "transparent",
                border: "1px solid #E9E9E8",
                borderRadius: "6px",
                px: 1.5,
                py: 0.5,
                minWidth: "100px",
                height: "32px",
                fontSize: "0.8125rem",
                boxShadow: "none",
                whiteSpace: "nowrap",
                flexShrink: 0,
                "&:hover": {
                  backgroundColor: "#F9F9F9",
                  cursor: "pointer",
                },
                "&:active": {
                  transform: "scale(0.98)",
                },
                transition: "all 0.2s ease-in-out",
              }}
            >
              <img
                src={viewIcon}
                alt="View"
                style={{ marginRight: 4, width: 16, height: 16 }}
              />
            </Button>
          )}
        </div>
        {pharmacyPhone && (
          <div
            style={{
              display: "flex",
              gap: 6,
              alignItems: "center",
              fontWeight: 500,
              fontSize: "14px",
              lineHeight: "150%",
              letterSpacing: "0%",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            <Tooltip title="Phone" arrow>
              <PhoneEnabledRoundedIcon
                style={{ fontSize: 16, minWidth: "16px" }}
              />
            </Tooltip>
            <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
              {pharmacyPhone}
            </span>
          </div>
        )}
      </div>
    );
  },
  enableSorting: true,
};

export const getPharmacyColumns = (onQuickView) => [
  pharmacyDetailsColumn,

  pharmacyMemberColumn,
  getOutreachColumn(true),
  attemptsColumn,
  programsColumn,
  getRecentActivityColumn(true),
  getlatestDateAddedColumn(true),
  actionsColumn(onQuickView),
];
export const providerDetailsColumn = {
  header: createHeaderWithSort("Provider Details", true),
  accessorKey: "provider",
  id: "providerDetails",
  size: 120,
  sortingFn: (rowA, rowB) => {
    const a = rowA.original.provider?.name || "";
    const b = rowB.original.provider?.name || "";
    return a.localeCompare(b);
  },
  cell: ({ row }) => {
    const provider =
      row.original.provider || row.original.providerDetails || row.original;

    const providerName =
      typeof provider === "object" && provider !== null
        ? provider.name || provider.provider || ""
        : provider;
    const providerPhone =
      typeof provider === "object" && provider !== null
        ? provider.contact || provider.providerPhone || ""
        : "";
    const onQuickView = row.original.onQuickView;

    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 4,
          minWidth: "120px",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            flexWrap: "nowrap",
            width: "100%",
          }}
        >
          <Tooltip
            title={providerName || ""}
            placement="bottom-start"
            arrow
            describeChild
            disableInteractive
            slotProps={{
              popper: {
                sx: {
                  "& .MuiTooltip-tooltip": {
                    backgroundColor: "#343435",
                    color: "#FFFFFF",
                    margin: 0,
                  },
                  "& .MuiTooltip-arrow": {
                    color: "#343435",
                    margin: 0,
                    left: "50% !important",
                    transform: "translateX(-50%) !important",
                  },
                },
              },
              tooltip: {
                sx: {
                  margin: 0,
                  marginTop: "0px !important",
                  padding: "4px 8px",
                },
              },
            }}
          >
            <Link
              to={`/providerProfile/${row.original.key}`}
              title={providerName}
              style={{
                fontWeight: 600,
                fontStyle: "Bold",
                fontSize: "17px",
                color: "#4b3f72",
                flexDirection: "column",
                textDecoration: "underline",
                textDecorationStyle: "solid",
                textDecorationThickness: 1,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                textDecorationSkipInk: "auto",
                width: "150px",
                minWidth: "150px",
                textUnderlineOffset: 3,
                cursor: "pointer",
                flex: 1,
                display: "inline-block",
                marginTop: "4px",
              }}
            >
              {capitalizeFirstLetter(providerName)}
            </Link>
          </Tooltip>
          {onQuickView && (
            <Button
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                onQuickView(row.original);
              }}
              sx={{
                textTransform: "none",
                fontWeight: 500,
                color: "#403C6F",
                backgroundColor: "transparent",
                border: "1px solid #E9E9E8",
                borderRadius: "6px",
                px: 1.5,
                py: 0.5,
                minWidth: "100px",
                height: "32px",
                fontSize: "0.8125rem",
                boxShadow: "none",
                whiteSpace: "nowrap",
                flexShrink: 0,
                "&:hover": {
                  backgroundColor: "#F9F9F9",
                  cursor: "pointer",
                },
                "&:active": {
                  transform: "scale(0.98)",
                },
                transition: "all 0.2s ease-in-out",
              }}
            >
              <img
                src={viewIcon}
                alt="View"
                style={{ marginRight: 4, width: 16, height: 16 }}
              />
            </Button>
          )}
        </div>
        {providerPhone && (
          <div
            style={{
              display: "flex",
              gap: 6,
              alignItems: "center",
              fontWeight: 500,
              fontSize: "14px",
              lineHeight: "150%",
              letterSpacing: "0%",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            <Tooltip title="Phone" arrow>
              <PhoneEnabledRoundedIcon
                style={{ fontSize: 16, minWidth: "16px" }}
              />
            </Tooltip>
            <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
              {providerPhone}
            </span>
          </div>
        )}
      </div>
    );
  },
  enableSorting: true,
};

export const getProviderColumns = (onQuickView) => [
  providerDetailsColumn,
  pharmacyMemberColumn,
  getOutreachColumn(true),
  attemptsColumn,
  programsColumn,
  getRecentActivityColumn(true),
  getlatestDateAddedColumn(true),
  actionsColumn(onQuickView),
];
